import React, { useEffect } from 'react'

import { useSelector } from 'react-redux'

import faviconDefault from '@/assets/images/favicon.png'
import {
	useCustomSettings,
	useCustomCSS,
} from '@/core/hooks/misc/useCustomSettings'
import { RootState } from '@/core/store'
import { getBuildType } from '@/core/utils/buildType'

/**
 * Component that applies partner customization settings to the application.
 * This includes:
 * - Custom logos (sidebar and login)
 * - Custom background image for login
 * - Custom favicon
 * - Custom CSS
 */
export const PartnerCustomizationProvider: React.FC<{
	children: React.ReactNode
}> = ({ children }) => {
	// Fetch custom settings and CSS
	const {
		data: customSettings,
		isLoading: isLoadingSettings,
		fetchStatus: settingsFetchStatus,
	} = useCustomSettings()
	const {
		data: customCSS,
		isLoading: isLoadingCSS,
		fetchStatus: cssFetchStatus,
	} = useCustomCSS()
	const buildType = getBuildType()
	const theme = useSelector((state: RootState) => state.ui.theme)

	useEffect(() => {
		// Apply custom favicon if available
		if (!isLoadingSettings) {
			// Try to find existing favicon link
			let favicon = document.querySelector(
				'link[rel="icon"]'
			) as HTMLLinkElement | null

			// If no favicon found, try different rel attributes
			if (!favicon) {
				favicon = document.querySelector(
					'link[rel="shortcut icon"]'
				) as HTMLLinkElement | null
			}

			// If still no favicon, create a new one
			if (!favicon) {
				favicon = document.createElement('link')
				favicon.rel = 'icon'
				favicon.type = 'image/x-icon'
				document.head.appendChild(favicon)
			}

			if (customSettings?.images?.favicon) {
				// Set the favicon href
				if (favicon) {
					favicon.setAttribute('href', customSettings.images.favicon)
				} else {
					console.error('Could not find or create favicon element')
				}
			} else {
				favicon.setAttribute('href', faviconDefault)
			}
		}
	}, [isLoadingSettings, customSettings])

	useEffect(() => {
		// Apply custom CSS if available
		if (!isLoadingCSS && customCSS) {
			// Remove any previously added custom style
			const existingStyle = document.getElementById('custom-partner-css')
			if (existingStyle) {
				existingStyle.remove()
			}

			// Add new custom style
			if (customCSS) {
				const style = document.createElement('style')
				style.id = 'custom-partner-css'
				style.textContent = customCSS
				document.head.appendChild(style)
			}
		}
	}, [isLoadingCSS, customCSS])

	// Apply partner-specific light theme overrides for ibiza_songs
	useEffect(() => {
		const existingPartnerTheme = document.getElementById(
			'partner-theme-overrides'
		)
		if (existingPartnerTheme) {
			existingPartnerTheme.remove()
		}

		// Only inject theme overrides for ibiza_songs build in light mode
		if (buildType === 'ibiza_songs' && theme === 'light') {
			const style = document.createElement('style')
			style.id = 'partner-theme-overrides'
			// TODO: Finish the theme overrides for Ibiza Songs
			// Override main accent to red tone for Ibiza, keeping it scoped to :root (light theme only)
			style.textContent = `
				:root {
					--main-accent: oklch(0.6 0.22 25);
					--main-accent-foreground: oklch(1 0 0);
				}
			`
			document.head.appendChild(style)
		}
	}, [buildType, theme])

	// For whitelabel builds, hold rendering until partner settings have resolved
	// (either with data or with an error/null) so users never see a flash of
	// Brandtrack branding before the partner logo is available.
	const isMusicbrand = buildType === 'musicbrand'
	const settingsSettled = !isLoadingSettings || settingsFetchStatus === 'idle'
	const cssSettled = !isLoadingCSS || cssFetchStatus === 'idle'
	if (isMusicbrand && (!settingsSettled || !cssSettled)) {
		return <div className="min-h-screen bg-background" />
	}

	return <>{children}</>
}

export default PartnerCustomizationProvider
